// Used by: PortalQuoteQueryJets, PortalDealListFilters
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import axios from "axios";
import getClassName from "../../utilities/getClassName";
import Fieldset from "../ui/Fieldset";

import debounce from "debounce-promise";
import envVariables from "../../data/envVariables.json";
import Button from "../ui/Button";

// Icons
import personIcon from "../../assets/icons/person-dark.svg";
import statusLeadIcon from "../../assets/icons/raw/status-lead.svg";
import statusClientIcon from "../../assets/icons/raw/status-client.svg";

const getStatusIcon = (zohoModule) => (zohoModule === "Leads" ? statusLeadIcon : statusClientIcon);

const formatLeadClientLabel = (option) => {
    const name = [option.firstName, option.lastName].filter(Boolean).join(" ").trim();
    const email = option.email ? ` (${option.email})` : "";

    return (
        <span className="lead-client-input__option">
            <img
                src={getStatusIcon(option.zohoModule)}
                alt={option.zohoModule === "Leads" ? "Lead" : "Client"}
                className="lead-client-input__status-icon"
            />
            <span>
                {name}
                {email}
            </span>
        </span>
    );
};

const LeadClientInput = ({
    direction,
    value,
    defaultValue,
    label,
    placeholder,
    validateForm,
    required,
    errorOnBlur,
    onFocus,
    copiedProperty,
    skeleton,
    showReset,
}) => {
    // State
    const [error, setError] = useState("");
    const [selectedValue, setSelectedValue] = useState(null);
    const [hideMenu, setHideMenu] = useState({ menuIsOpen: false });

    // Mount-only: seed from `copiedProperty` if the parent passed one.
    // Adding validationHandler/value to deps would loop because parents
    // pass inline arrows; copiedProperty is a one-shot init value.
    useEffect(() => {
        if (copiedProperty) {
            setSelectedValue(copiedProperty ? copiedProperty : selectedValue);
            validationHandler(copiedProperty ? copiedProperty : selectedValue);
            value(copiedProperty ? copiedProperty : selectedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle selection change
    const selectChangeHandler = (selected) => {
        setSelectedValue(selected);
        validationHandler(selected);
        value(selected);
    };

    // Validate input
    const validationHandler = (validateSelection) => {
        switch (true) {
            case required && !validateSelection:
                setError(`${label} is required`);
                return;
            case required && validateSelection.length === 0:
                setError(`${label} is required`);
                return;
            default:
                setError("");
                return;
        }
    };

    const fetchLeadClientsByName = (searchTerm) => {
        const queryString = `fname=${encodeURIComponent(searchTerm)}`;

        return axios
            .get(`${envVariables.apiEndpoint}/api/portal/leadclient-info-by-name?${queryString}`)
            .then((response) => response.data || []);
    };

    const filterBySearchTokens = (results, tokens) => {
        if (tokens.length <= 1) {
            return results;
        }

        return results.filter((item) => {
            const fullName = `${item.firstName || ""} ${item.lastName || ""}`.toLowerCase();
            return tokens.every((token) => fullName.includes(token));
        });
    };

    // Load options using API call
    const debounceLoadOptions = debounce(async (inputValue) => {
        if (inputValue.length < 3) {
            setHideMenu({ menuIsOpen: false });
            return;
        }

        setHideMenu({});

        const searchTerm = inputValue.trim();
        const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
        let results = await fetchLeadClientsByName(searchTerm);

        results = filterBySearchTokens(results, tokens);

        if (results.length === 0 && tokens.length > 1) {
            const primaryToken = tokens.reduce((longest, token) => (token.length > longest.length ? token : longest));
            results = await fetchLeadClientsByName(primaryToken);
            results = filterBySearchTokens(results, tokens);
        }

        return results;
    }, 300);

    // Set default value
    useEffect(() => {
        if (defaultValue) {
            setSelectedValue(defaultValue);
        } else {
            setSelectedValue(null);
        }
    }, [defaultValue]);

    // Force validation
    useEffect(() => {
        if (validateForm) {
            validationHandler(selectedValue);
        }
    });

    const focus = () => {
        if (onFocus) {
            onFocus();
        }
    };

    const getValue = () => {
        let value;

        if (copiedProperty) {
            value = copiedProperty;
        }

        if (selectedValue) {
            value = selectedValue;
        }

        return value;
    };

    const doReset = () => {
        selectChangeHandler(null);
    };

    return (
        <>
            <Fieldset skeleton={skeleton}>
                <div
                    style={{ backgroundImage: `url(${personIcon})` }}
                    className={getClassName([
                        "select-input",
                        "select-input--from-to",
                        "select-input--flight-enquiry",
                        "select-input--full",
                        error && "select-input--flight-enquiry--error",
                    ])}
                >
                    <AsyncSelect
                        aria-label={label || placeholder || "Lead/Client"}
                        noOptionsMessage={() => "No Leads/Clients Found"}
                        classNamePrefix="select-input"
                        cacheOptions={false}
                        defaultOptions={false}
                        value={getValue()}
                        formatOptionLabel={formatLeadClientLabel}
                        getOptionValue={(e) => {
                            return {
                                zohoId: e.zohoId,
                                zohoModule: e.zohoModule,
                            };
                        }}
                        loadOptions={debounceLoadOptions}
                        onFocus={focus}
                        onChange={selectChangeHandler}
                        onBlur={errorOnBlur ? () => validationHandler(selectedValue) : () => {}}
                        placeholder={placeholder || "Lead/Client"}
                        isClearable={true}
                        {...hideMenu}
                    />
                </div>
            </Fieldset>
            {showReset && <Button onClick={doReset}>Reset</Button>}
        </>
    );
};

export default LeadClientInput;
